import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  PortalPermissions,
  useFeatureSubscription_GetFeatureSubscriptionTypeQuery,
  useUser_GetPortalOwnerByPortalIdQuery,
} from 'queries';
import { ChevronRight } from 'ts-react-feather-icons';

import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import { canCreateFirmSpecificTasks } from 'utils/feature-checker';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';
import useCurrentUser from 'utils/useCurrentUser';
import useHasPortalPermission from 'utils/useHasPortalPermission';
import useIsImpersonating from 'utils/useIsImpersonating';

import ChangeCurrentTaskCreditModal from './components/change-current-task-credit-modal';
import ChangePortalLogoModal from './components/change-portal-logo-modal';
import ChangePortalNameModal from './components/change-portal-name-modal';
import ManageCustomTasksModal from './components/manage-custom-tasks-modal';
import ManagePortalAccessTokens from './components/manage-portal-access-tokens';
import ManagePortalUsers from './components/manage-portal-users';
import PortalBillingHistory from './components/portal-billing-history';
import PortalSubscriptionBillingHistory from './components/portal-subscription-billing-history';

const useStyles = makeStyles((theme) => ({
  root: {
    // no border around the cells when focused
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  container: {
    padding: '5%',
    flexGrow: 1,
  },
  title: {
    fontWeight: 600,
  },
  routeIconContainer: {
    verticalAlign: 'middle',
  },
  cancelSubText: {
    marginBottom: theme.spacing(1),
  },
}));

export default function PortalAccountPage() {
  const classes = useStyles();
  const history = useHistory();
  const { portalId } = useParams<Record<string, string>>();

  const [showChangePortalNameModal, setShowChangePortalNameModal] = useState(false);
  const [showChangeCurrentTaskCreditModal, setShowChangeCurrentTaskCreditModal] = useState(false);
  const [showChangePortalLogoModal, setShowChangePortalLogoModal] = useState(false);
  const [showManageCustomTasksModal, setShowManageCustomTasksModal] = useState<boolean>(false);

  const currentUser = useCurrentUser();
  const isImpersonating = useIsImpersonating();
  const hasPortalPermissionAdmin = useHasPortalPermission(PortalPermissions.Admin);

  const { data: featureSubscriptionTypeData, loading: featureSubscriptionTypeLoading } =
    useFeatureSubscription_GetFeatureSubscriptionTypeQuery({
      variables: {
        portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting feature subscription type: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const { data: portalOwnerData, loading: portalOwnerLoading } =
    useUser_GetPortalOwnerByPortalIdQuery({
      variables: {
        portalId: portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting portal owner: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const onIntegrationsButtonClick = () => () => {
    history.push(`/home/${portalId}/portal-account/integrations`);
  };

  if (!currentUser) {
    return <Spinner />;
  }
  if (!featureSubscriptionTypeData || featureSubscriptionTypeLoading) {
    return <Spinner />;
  }
  if (!portalOwnerData || portalOwnerLoading) {
    return <Spinner />;
  }

  return (
    <HomeLayout showPortalName={true}>
      <>
        {isImpersonating && (
          <>
            <ChangePortalNameModal
              showModal={showChangePortalNameModal}
              setShowModal={setShowChangePortalNameModal}
            />
            <ChangeCurrentTaskCreditModal
              showModal={showChangeCurrentTaskCreditModal}
              setShowModal={setShowChangeCurrentTaskCreditModal}
            />
          </>
        )}
        {hasPortalPermissionAdmin && (
          <>
            <ChangePortalLogoModal
              showModal={showChangePortalLogoModal}
              setShowModal={setShowChangePortalLogoModal}
            />
            <ManageCustomTasksModal
              showModal={showManageCustomTasksModal}
              setShowModal={setShowManageCustomTasksModal}
            />
          </>
        )}
        <div className={classes.container}>
          <div>
            {isImpersonating && (
              <>
                <span>Impersonation Actions: </span>
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ marginTop: '3px', padding: '2px 10px' }}
                    onClick={() => {
                      setShowChangePortalNameModal(true);
                    }}
                  >
                    Change Portal Name
                  </Button>
                </div>
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ marginTop: '3px', padding: '2px 10px' }}
                    onClick={() => {
                      setShowChangeCurrentTaskCreditModal(true);
                    }}
                  >
                    Change Current Task Credit
                  </Button>
                </div>
                <hr />
              </>
            )}
          </div>
          <div className={classes.cancelSubText}>
            To cancel your Trustate subscription, please complete the cancellation request
            questionnaire, here →{' '}
            <a href="https://share.hsforms.com/2EeC_ecIfSxyBBHpENQWgGgckwb4">
              Submit my cancellation request
            </a>
            .
          </div>
          <div style={{ display: 'flex', verticalAlign: 'bottom' }}>
            <Typography className={classes.title} variant="h5" style={{ flex: 3 }}>
              <span
                onClick={() => {
                  history.push(`/home/${portalId}/home`);
                }}
              >
                <span className={classes.routeIconContainer}>
                  <img src="/home-icon.svg" alt="home-icon" />
                </span>
                <span className={classes.routeIconContainer}>
                  <ChevronRight />
                </span>
              </span>
              Account
            </Typography>
            {hasPortalPermissionAdmin && (
              <>
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ marginTop: '3px', marginLeft: '3px', padding: '2px 10px' }}
                    onClick={() => setShowChangePortalLogoModal(true)}
                  >
                    Change Portal Logo
                  </Button>
                </div>
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ marginTop: '3px', marginLeft: '3px', padding: '2px 10px' }}
                    onClick={() => {
                      if (!canCreateFirmSpecificTasks(featureSubscriptionTypeData!.data)) {
                        showToast({
                          message: 'Please upgrade your account to manage custom tasks.',
                          severity: NotificationSeverity.WARNING,
                        });
                        return;
                      }

                      setShowManageCustomTasksModal(true);
                    }}
                  >
                    Manage Custom Tasks
                  </Button>
                </div>
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ marginTop: '3px', marginLeft: '3px', padding: '2px 10px' }}
                    onClick={onIntegrationsButtonClick()}
                  >
                    Integrations
                  </Button>
                </div>
              </>
            )}
          </div>
          <hr />
          <ManagePortalUsers
            featureSubscriptionType={featureSubscriptionTypeData!.data}
            portalOwner={portalOwnerData!.data}
          />
          <hr />
          {hasPortalPermissionAdmin && <ManagePortalAccessTokens />}
          <hr />
          <PortalBillingHistory />
          <hr />
          {currentUser!.id === portalOwnerData!.data.id && <PortalSubscriptionBillingHistory />}
        </div>
      </>
    </HomeLayout>
  );
}
